import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../../../helpers/axios';

import {
  Box, Chip, LinearProgress, Skeleton, Alert, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, InputAdornment, Tooltip, CircularProgress,
  Menu, MenuItem, Snackbar, Badge,
  Select, FormControl, InputLabel, FormControlLabel, Checkbox,
} from '@mui/material';
import PaginationTable from '../../status/PaginationTable';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SmsIcon from '@mui/icons-material/Sms';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CampaignIcon from '@mui/icons-material/Campaign';
import PaymentLinkModal from './PaymentLinkModal';

// ─── Constants ────────────────────────────────────────────────────────────────

const BUCKET_COLOR = {
  S1: { bg: '#e8f5e9', text: '#2e7d32', border: '#a5d6a7' },
  S2: { bg: '#fff8e1', text: '#f57f17', border: '#ffe082' },
  S3: { bg: '#fff3e0', text: '#e65100', border: '#ffcc80' },
  M1: { bg: '#fce4ec', text: '#c62828', border: '#f48fb1' },
  M2: { bg: '#f3e5f5', text: '#6a1b9a', border: '#ce93d8' },
  X1: { bg: '#e8eaf6', text: '#283593', border: '#9fa8da' },
  X2: { bg: '#e0e0e0', text: '#424242', border: '#bdbdbd' },
  WO: { bg: '#212121', text: '#ffffff', border: '#424242' },
};

const BUCKET_ORDER = ['S1', 'S2', 'S3', 'M1', 'M2', 'X1', 'X2', 'WO'];

const DISP_BADGE = {
  PTP: { bg: '#FFF9C4', text: '#F57F17' },
  RNR: { bg: '#fce4ec', text: '#c62828' },
  CB:  { bg: '#e8f5e9', text: '#2e7d32' },
};

const DISP_COLOR = {
  'PTP':                { bg: '#e8f5e9', text: '#2e7d32',  border: '#a5d6a7',  dot: '#2e7d32'  },
  'Full':               { bg: '#d4edda', text: '#1b5e20',  border: '#81c784',  dot: '#1b5e20'  },
  'Full Payment':       { bg: '#d4edda', text: '#1b5e20',  border: '#81c784',  dot: '#1b5e20'  },
  'Partial':            { bg: '#fff8e1', text: '#f57f17',  border: '#ffe082',  dot: '#f57f17'  },
  'Partial Payment':    { bg: '#fff8e1', text: '#f57f17',  border: '#ffe082',  dot: '#f57f17'  },
  'No Payment':         { bg: '#ffebee', text: '#c62828',  border: '#ef9a9a',  dot: '#c62828'  },
  'Not Contactable':    { bg: '#ffebee', text: '#c62828',  border: '#ef9a9a',  dot: '#c62828'  },
  'Refused':            { bg: '#f3e5f5', text: '#6a1b9a',  border: '#ce93d8',  dot: '#6a1b9a'  },
  'Settlement Offered': { bg: '#e8eaf6', text: '#283593',  border: '#9fa8da',  dot: '#283593'  },
  'RNR':                { bg: '#fff3e0', text: '#e65100',  border: '#ffcc80',  dot: '#e65100'  },
  'CB':                 { bg: '#e3f2fd', text: '#1565c0',  border: '#90caf9',  dot: '#1565c0'  },
};

// Notification menu options — defined once, no re-creation on render
const NOTIFY_OPTIONS = [
  {
    type: 'sms',
    label: 'SMS Reminder',
    desc: 'Send payment reminder via text message',
    icon: <SmsIcon sx={{ fontSize: 17, color: '#1976d2' }} />,
    iconBg: '#e3f2fd',
    hoverBg: '#f0f7ff',
  },
  {
    type: 'whatsapp',
    label: 'WhatsApp Reminder',
    desc: 'Send payment reminder via WhatsApp',
    icon: <WhatsAppIcon sx={{ fontSize: 17, color: '#25d366' }} />,
    iconBg: '#e8f5e9',
    hoverBg: '#f0fff4',
  },
  {
    type: 'overdue_notice',
    label: 'Overdue Notice',
    desc: 'Send formal overdue notice to borrower',
    icon: <CampaignIcon sx={{ fontSize: 17, color: '#D22129' }} />,
    iconBg: '#ffebee',
    hoverBg: '#fff5f5',
  },
];

// ─── Formatters ───────────────────────────────────────────────────────────────

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
const countDispositions = (rows) => {
  const counts = {};
  rows.forEach((l) => { if (l.lastDisposition) counts[l.lastDisposition] = (counts[l.lastDisposition] || 0) + 1; });
  return counts;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatPill({ label, value, color = '#344054' }) {
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      px: 2, borderRight: '1px solid #e0e0e0',
      '&:last-child': { borderRight: 'none' },
    }}>
      <Typography sx={{ fontSize: '18px', fontWeight: 700, color, lineHeight: 1.2 }}>{value}</Typography>
      <Typography sx={{ fontSize: '10px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.4px', mt: '2px' }}>{label}</Typography>
    </Box>
  );
}

function RecoveredPill({ collected, monthlyRecovered }) {
  const now = new Date();
  const monthName = now.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  const todayStr = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const tooltipContent = (
    <Box sx={{ p: '4px 2px' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', mb: '8px' }}>
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#4caf50', flexShrink: 0, mt: '1px' }} />
        <Box>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
            {fmt(monthlyRecovered ?? 0)}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.3 }}>
            total recovered in {monthName}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#81c784', flexShrink: 0, mt: '1px' }} />
        <Box>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
            {fmt(collected)}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.3 }}>
            recovered as of today (from assigned loans)
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="bottom"
      componentsProps={{
        tooltip: { sx: { backgroundColor: '#1e2a38', maxWidth: 260, borderRadius: '8px', p: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.18)' } },
        arrow: { sx: { color: '#1e2a38' } },
      }}
    >
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        px: 2, borderRight: '1px solid #e0e0e0', cursor: 'default', minWidth: 0,
      }}>
        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#2e7d32', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
          {fmt(monthlyRecovered ?? 0)}
        </Typography>
        <Typography sx={{ fontSize: '10px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.4px', mt: '2px' }}>
          Recovered
        </Typography>
        <Typography sx={{ fontSize: '10px', color: '#b0b8c4', lineHeight: 1.3, mt: '1px', whiteSpace: 'nowrap' }}>
          {fmt(collected)} · {todayStr}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function HandoffHistoryPanel({ history, loading }) {
  if (loading) {
    return (
      <Box style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 0' }}>
        <CircularProgress size={18} style={{ color: '#D22129' }} />
        <Typography style={{ fontSize: '13px', color: '#9e9e9e' }}>Loading history…</Typography>
      </Box>
    );
  }

  if (!history || history.length === 0) {
    return (
      <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 0', color: '#9e9e9e' }}>
        <HistoryIcon style={{ fontSize: '16px' }} />
        <Typography style={{ fontSize: '13px' }}>No previous handoff records found for this loan.</Typography>
      </Box>
    );
  }

  return (
    <Box style={{ padding: '14px 0 4px 0' }}>
      <Box style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '14px' }}>
        <HistoryIcon style={{ fontSize: '15px', color: '#667085' }} />
        <Typography style={{ fontSize: '12px', fontWeight: 700, color: '#344054', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Handoff History
        </Typography>
        <Box style={{
          marginLeft: '4px', padding: '1px 8px', borderRadius: '20px',
          backgroundColor: '#f0f0f0', fontSize: '11px', fontWeight: 700, color: '#667085',
        }}>
          {history.length} record{history.length !== 1 ? 's' : ''}
        </Box>
      </Box>

      <Box style={{ position: 'relative', paddingLeft: '18px', borderLeft: '2px solid #e0e0e0' }}>
        {history.map((item, idx) => {
          const dc = DISP_COLOR[item.finalDisposition] || { bg: '#f5f5f5', text: '#344054', border: '#e0e0e0', dot: '#9e9e9e' };
          const isLast = idx === history.length - 1;
          return (
            <Box key={idx} style={{ position: 'relative', paddingLeft: '20px', paddingBottom: isLast ? 0 : '18px' }}>
              <Box style={{
                position: 'absolute', left: '-25px', top: '5px',
                width: '10px', height: '10px', borderRadius: '50%',
                backgroundColor: dc.dot, border: '2px solid white',
                boxShadow: `0 0 0 2px ${dc.border}`,
              }} />
              <Box style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '4px' }}>
                <Typography style={{ fontSize: '13px', fontWeight: 700, color: '#344054' }}>
                  {item.fromAgentId || 'Unknown Agent'}
                </Typography>
                <Typography style={{ fontSize: '11px', color: '#9e9e9e' }}>
                  {fmtDate(item.allocationPeriodStart)} → {fmtDate(item.allocationPeriodEnd)}
                </Typography>
                {item.finalDisposition && (
                  <Box style={{
                    padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                    backgroundColor: dc.bg, color: dc.text, border: `1px solid ${dc.border}`, whiteSpace: 'nowrap',
                  }}>
                    {item.finalDisposition}
                  </Box>
                )}
              </Box>
              {item.outcomeSummary && (
                <Typography style={{ fontSize: '12px', color: '#667085', marginBottom: '3px' }}>{item.outcomeSummary}</Typography>
              )}
              {item.reason && (
                <Typography style={{ fontSize: '11px', color: '#9e9e9e', marginBottom: '3px' }}>Reason: {item.reason}</Typography>
              )}
              {item.recommendation && (
                <Box style={{
                  display: 'inline-flex', alignItems: 'flex-start', gap: '4px',
                  backgroundColor: '#e3f2fd', border: '1px solid #90caf9',
                  borderRadius: '6px', padding: '4px 10px', marginTop: '4px',
                }}>
                  <Typography style={{ fontSize: '11px', color: '#1565c0', fontWeight: 600 }}>Recommendation:</Typography>
                  <Typography style={{ fontSize: '11px', color: '#1565c0' }}>{item.recommendation}</Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AgentLoans({ agentId, initialData }) {
  const initLoans = !agentId && initialData?.loansRaw ? (Array.isArray(initialData.loansRaw) ? initialData.loansRaw : (initialData.loansRaw.data || [])) : [];
  const [loans, setLoans]           = useState(initLoans);
  const [loading, setLoading]       = useState(agentId ? true : !initialData);
  const [error, setError]           = useState(null);
  const [activeBucket, setActiveBucket] = useState('All');
  const [currentPage, setCurrentPage]   = useState(1);
  const [rowsPerPage, setRowsPerPage]   = useState(15);
  const [totalFetchRows, setTotalFetchRows] = useState(
    !agentId && initialData?.loansRaw?.pagination ? initialData.loansRaw.pagination.totalRows : 0
  );
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  // PTP dialog
  const [ptpDialog, setPtpDialog] = useState({ open: false, loan: null });
  const [ptpDate, setPtpDate] = useState('');
  const [ptpLoading, setPtpLoading] = useState(false);

  // Payment link modal
  const [paymentLinkModal, setPaymentLinkModal] = useState({ open: false, loan: null });

  // Handoff history
  const [expandedLoanId, setExpandedLoanId] = useState(null);
  const [historyMap, setHistoryMap] = useState({});
  const [historyLoading, setHistoryLoading] = useState({});

  // Notification bell — anchor tracks which bell is open, sendingId tracks in-flight request
  const [notifyMenu, setNotifyMenu] = useState({ anchor: null, loan: null });
  const [sendingNotifyId, setSendingNotifyId] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [localDispositions, setLocalDispositions] = useState({});
  const [monthlyRecovered, setMonthlyRecovered] = useState(
    !agentId && initialData?.dashboardRaw ? (initialData.dashboardRaw?.stats?.recovered ?? 0) : null
  );
  // Grand totals across all pages — populated from API response, not recalculated per page
  const [grandTotals, setGrandTotals] = useState(null);
  const [apiBucketCounts, setApiBucketCounts] = useState(null);

  const [activeDisposition, setActiveDisposition] = useState('All');
  const [apiDispositionCounts, setApiDispositionCounts] = useState(null);
  const [callModal, setCallModal] = useState({ open: false, loan: null });
  const [callDate, setCallDate] = useState('');
  const [callContact, setCallContact] = useState('');
  const [callPhone, setCallPhone] = useState('');
  const [callStatus, setCallStatus] = useState('RNR');
  const [callRemarks, setCallRemarks] = useState('');
  const [callReminder, setCallReminder] = useState(true);
  const [callPtpDate, setCallPtpDate] = useState('');
  const [callSubmitting, setCallSubmitting] = useState(false);

  const isManagerPeek = Boolean(agentId);

  useEffect(() => {
    if (!isManagerPeek && initialData && currentPage === 1 && activeBucket === 'All') return;

    setLoading(true);
    const bucketParam = activeBucket !== 'All' ? `&bucket=${activeBucket}` : '';
    const dispositionParam = activeDisposition !== 'All' ? `&disposition=${activeDisposition}` : '';
    const url = isManagerPeek
      ? `/collection-incentive/manager/agents/${agentId}/loans?pageNo=${currentPage}&perPage=${rowsPerPage}${bucketParam}${dispositionParam}`
      : `/collection-incentive/agent/loans?pageNo=${currentPage}&perPage=${rowsPerPage}${bucketParam}${dispositionParam}`;

    const loansCall = axiosInstance.get(url)
      .then((res) => {
        const result = res.data.data;
        if (Array.isArray(result)) {
          setLoans(result);
          setTotalFetchRows(result.length);
        } else {
          setLoans(result.data || []);
          setCurrentPage(result.pagination?.currentPage || currentPage);
          setTotalFetchRows(result.pagination?.totalRows || 0);
          setRowsPerPage(result.pagination?.perPage || 15);
          if (result.totals) setGrandTotals(result.totals);
          if (result.bucketCounts && activeBucket === 'All') setApiBucketCounts(result.bucketCounts);
          const dispCounts = result.dispositionCounts || countDispositions(result.data || []);
          setApiDispositionCounts(dispCounts);
        }
      })
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load loans'));

    if (currentPage === 1 && activeBucket === 'All') {
      const dashboardUrl = isManagerPeek
        ? `/collection-incentive/manager/agents/${agentId}/dashboard`
        : '/collection-incentive/agent/dashboard';
      const dashboardCall = axiosInstance.get(dashboardUrl)
        .then((res) => setMonthlyRecovered(res.data.data?.stats?.recovered ?? 0))
        .catch(() => {});
      Promise.all([loansCall, dashboardCall]).finally(() => setLoading(false));
    } else {
      loansCall.finally(() => setLoading(false));
    }
  }, [agentId, isManagerPeek, currentPage, activeBucket, activeDisposition, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isManagerPeek || !initialData) return;
    const raw = initialData.loansRaw;
    if (raw) {
      setLoans(Array.isArray(raw) ? raw : (raw.data || []));
      if (raw.pagination) {
        setTotalFetchRows(raw.pagination.totalRows || 0);
        setRowsPerPage(raw.pagination.perPage || 15);
      }
      if (raw.totals) setGrandTotals(raw.totals);
      if (raw.bucketCounts) setApiBucketCounts(raw.bucketCounts);
      const dispCounts = raw.dispositionCounts || countDispositions(raw.data || []);
      setApiDispositionCounts(dispCounts);
    }
    setMonthlyRecovered(initialData.dashboardRaw?.stats?.recovered ?? 0);
    setLoading(false);
  }, [initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  const bucketCounts = useMemo(() => {
    const map = { All: grandTotals?.totalLoans || totalFetchRows };
    for (const b of BUCKET_ORDER) map[b] = apiBucketCounts ? (apiBucketCounts[b] || 0) : loans.filter((l) => l.bucket === b).length;
    return map;
  }, [loans, grandTotals, totalFetchRows, apiBucketCounts]);

  const filteredLoans = useMemo(() => {
    if (!search.trim()) return loans;
    const q = search.toLowerCase();
    return loans.filter((l) =>
      (l.borrowerName || '').toLowerCase().includes(q) ||
      (l.legacyLoanId || '').toLowerCase().includes(q) ||
      (l.loanId || '').toLowerCase().includes(q) ||
      (l.phone || '').includes(q)
    );
  }, [loans, search]);

  const stats = useMemo(() => {
    // When searching, compute from filtered page results; otherwise use grand totals from API
    if (search.trim() || !grandTotals) {
      const loansCount = filteredLoans.length;
      const overdue = filteredLoans.reduce((s, l) => s + (Number(l.totalOverdue) || 0), 0);
      const collected = filteredLoans.reduce((s, l) => s + (Number(l.totalCollected) || 0), 0);
      const paid = filteredLoans.filter((l) => Number(l.totalCollected) > 0).length;
      const pct = overdue + collected > 0 ? Math.round((collected / (overdue + collected)) * 100) : 0;
      return { loansCount, overdue, collected, pct, paid };
    }
    const { totalLoans, totalOverdue, totalCollected, paidCount } = grandTotals;
    const pct = totalOverdue + totalCollected > 0 ? Math.round((totalCollected / (totalOverdue + totalCollected)) * 100) : 0;
    return { loansCount: totalLoans, overdue: totalOverdue, collected: totalCollected, pct, paid: paidCount };
  }, [filteredLoans, grandTotals, search]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleExport = () => {
    if (filteredLoans.length === 0) return;
    setExporting(true);
    const cols = ['Loan ID', 'Legacy Loan ID', 'Borrower Name', 'Phone', 'Bucket', 'DPD', 'Overdue (INR)', 'Collected (INR)', 'Last Paid', 'POS (INR)', 'Allocated Date'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [
      cols.join(','),
      ...filteredLoans.map((l) => [
        esc(l.legacyLoanId || l.loanId), esc(l.legacyLoanId), esc(l.borrowerName),
        esc(l.phone), esc(l.bucket), esc(l.dpd), esc(l.totalOverdue), esc(l.totalCollected),
        esc(fmtDate(l.lastPaidDate)), esc(l.pos),
        esc(l.allocatedAt ? new Date(l.allocatedAt).toLocaleDateString('en-IN') : ''),
      ].join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `loans-${activeBucket}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setExporting(false);
  };

  const handlePtp = (loan) => { setPtpDate(''); setPtpDialog({ open: true, loan }); };

  const handlePtpSubmit = async () => {
    if (!ptpDate) return;
    setPtpLoading(true);
    try {
      await axiosInstance.post(`/collection-incentive/agent/loans/${ptpDialog.loan.loanId}/disposition`, {
        type: 'PTP', promiseDate: ptpDate,
      });
      setLocalDispositions((prev) => ({ ...prev, [ptpDialog.loan.loanId]: 'PTP' }));
      setPtpDialog({ open: false, loan: null });
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to mark PTP');
    } finally {
      setPtpLoading(false);
    }
  };

  const handleRnr = async (loan) => {
    try {
      await axiosInstance.post(`/collection-incentive/agent/loans/${loan.loanId}/disposition`, { type: 'RNR' });
      setLocalDispositions((prev) => ({ ...prev, [loan.loanId]: 'RNR' }));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to mark RNR');
    }
  };

  const handleCb = async (loan) => {
    try {
      await axiosInstance.post(`/collection-incentive/agent/loans/${loan.loanId}/disposition`, { type: 'CB' });
      setLocalDispositions((prev) => ({ ...prev, [loan.loanId]: 'CB' }));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to mark CB');
    }
  };

  const handleLoanIdClick = (loan) => {
    const id = loan.loanId;
    if (expandedLoanId === id) { setExpandedLoanId(null); return; }
    setExpandedLoanId(id);
    if (historyMap[id] !== undefined) return;
    setHistoryLoading((prev) => ({ ...prev, [id]: true }));
    axiosInstance
      .get(`/collection-incentive/agent/loans/${id}/handoff-history`)
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setHistoryMap((prev) => ({ ...prev, [id]: Array.isArray(data) ? data : [] }));
      })
      .catch(() => setHistoryMap((prev) => ({ ...prev, [id]: [] })))
      .finally(() => setHistoryLoading((prev) => ({ ...prev, [id]: false })));
  };

  const handleSendNotification = (type) => {
    const loan = notifyMenu.loan;
    const opt = NOTIFY_OPTIONS.find((o) => o.type === type);
    setNotifyMenu({ anchor: null, loan: null });

    if (!loan.userId) {
      setToast({ open: true, message: 'Borrower ID not available for this loan', severity: 'error' });
      return;
    }

    setSendingNotifyId(loan.loanId);
    axiosInstance
      .post('/collection-notification/t-plus-1-to-30', { userId: loan.userId })
      .then((res) => {
        if (res?.data?.message === 'Successful') {
          setToast({
            open: true,
            message: `${opt.label} sent to ${loan.borrowerName?.trim() || 'borrower'} ✓`,
            severity: 'success',
          });
        }
      })
      .catch((err) => {
        setToast({
          open: true,
          message: err?.response?.data?.message || `Failed to send ${opt.label}`,
          severity: 'error',
        });
      })
      .finally(() => setSendingNotifyId(null));
  };

  const handleOpenCallModal = (loan) => {
    const now = new Date();
    const localNow = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setCallDate(localNow);
    setCallContact(loan.borrowerName?.trim() || '');
    setCallPhone(loan.phone || '');
    setCallStatus('RNR');
    setCallRemarks('');
    setCallReminder(true);
    setCallPtpDate('');
    setCallModal({ open: true, loan });
  };

  const handleCallSubmit = async () => {
    const loan = callModal.loan;
    if (!loan) return;
    setCallSubmitting(true);
    try {
      const payload = callStatus === 'PTP'
        ? { type: 'PTP', promiseDate: callPtpDate }
        : { type: callStatus };
      await axiosInstance.post(`/collection-incentive/agent/loans/${loan.loanId}/disposition`, payload);
      setLocalDispositions((prev) => ({ ...prev, [loan.loanId]: callStatus }));
      if (callReminder && callDate) {
        await axiosInstance.post(`/collection-incentive/agent/loans/${loan.loanId}/reminder`, {
          reminderDate: callDate,
          contactName: callContact,
          contactNumber: callPhone,
          status: callStatus,
          remarks: callRemarks,
        }).catch(() => {});
      }
      setToast({ open: true, message: `Call logged as ${callStatus} ✓`, severity: 'success' });
      setCallModal({ open: false, loan: null });
    } catch (err) {
      setToast({ open: true, message: err?.response?.data?.message || 'Failed to log call', severity: 'error' });
    } finally {
      setCallSubmitting(false);
    }
  };

  // ── Early returns ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rounded" height={44} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={60} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;

  // +1 for notify bell column added to both agent and manager views
  const colCount = isManagerPeek ? 13 : 15;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: '10px' }}>

      {/* ── Bucket Filter Tabs ── */}
      <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mb: '12px' }}>
        {['All', ...BUCKET_ORDER].map((b) => {
          const bc = BUCKET_COLOR[b] || {};
          const isActive = activeBucket === b;
          return (
            <Box key={b} onClick={() => { setActiveBucket(b); setCurrentPage(1); setActiveDisposition('All'); }} sx={{
              cursor: 'pointer', px: '12px', py: '6px', borderRadius: '20px', fontSize: '12px',
              fontWeight: isActive ? 700 : 500,
              border: isActive ? `2px solid ${b === 'All' ? '#d32028' : bc.border}` : '1px solid #e0e0e0',
              backgroundColor: isActive ? (b === 'All' ? '#fff0f0' : bc.bg) : 'white',
              color: isActive ? (b === 'All' ? '#d32028' : bc.text) : '#667085',
              display: 'flex', alignItems: 'center', gap: '6px',
              transition: 'all 0.15s', userSelect: 'none',
            }}>
              {b}
              <Box sx={{
                fontSize: '11px', fontWeight: 700,
                backgroundColor: isActive ? (b === 'All' ? '#d32028' : bc.text) : '#e0e0e0',
                color: 'white', borderRadius: '10px', px: '6px', py: '1px', lineHeight: 1.4,
              }}>
                {bucketCounts[b] || 0}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ── Disposition Filter Chips ── */}
      <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mb: '12px' }}>
        {['All', 'PTP', 'RNR', 'CB'].map((d) => {
          const DC = { PTP: { bg: '#f3e5f5', text: '#6a1b9a', border: '#ce93d8' }, RNR: { bg: '#ffebee', text: '#c62828', border: '#ef9a9a' }, CB: { bg: '#e3f2fd', text: '#1565c0', border: '#90caf9' } };
          const dc = DC[d] || {};
          const isActive = activeDisposition === d;
          const count = d === 'All' ? loans.length : (apiDispositionCounts?.[d] || 0);
          return (
            <Box key={d} onClick={() => { setActiveDisposition(d); setCurrentPage(1); }} sx={{
              cursor: 'pointer', px: '12px', py: '6px', borderRadius: '20px', fontSize: '12px',
              fontWeight: isActive ? 700 : 500,
              border: isActive ? `2px solid ${d === 'All' ? '#667085' : dc.border}` : '1px solid #e0e0e0',
              backgroundColor: isActive ? (d === 'All' ? '#f5f5f5' : dc.bg) : 'white',
              color: isActive ? (d === 'All' ? '#344054' : dc.text) : '#667085',
              display: 'flex', alignItems: 'center', gap: '6px',
              transition: 'all 0.15s', userSelect: 'none',
            }}>
              {d}
              <Box sx={{
                fontSize: '11px', fontWeight: 700,
                backgroundColor: isActive ? (d === 'All' ? '#667085' : dc.text) : '#e0e0e0',
                color: 'white', borderRadius: '10px', px: '6px', py: '1px', lineHeight: 1.4,
              }}>
                {count}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ── Stats Bar ── */}
      <Box sx={{
        display: 'flex', border: '1px solid #e0e0e0', borderRadius: 2,
        backgroundColor: 'white', mb: '12px', py: 1.5, overflowX: 'auto',
      }}>
        <StatPill label="Loans" value={stats.loansCount} color="#344054" />
        <StatPill label="Overdue" value={fmt(stats.overdue)} color="#d32028" />
        <RecoveredPill collected={stats.collected} monthlyRecovered={monthlyRecovered} />
        <StatPill
          label="Recovery %"
          value={`${stats.pct}%`}
          color={stats.pct >= 75 ? '#2e7d32' : stats.pct >= 40 ? '#f57f17' : '#d32028'}
        />
        <StatPill label="Paid Count" value={stats.paid} color="#1976d2" />
      </Box>

      {/* ── Search + Export Bar ── */}
      <Box sx={{ display: 'flex', gap: 1, mb: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search borrower, loan ID, phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { fontSize: '13px', borderRadius: '8px' } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#9e9e9e' }} /></InputAdornment>,
          }}
        />
        {search && (
          <Button size="small" onClick={() => setSearch('')} variant="outlined"
            sx={{ textTransform: 'none', fontSize: '12px', color: '#667085', borderColor: '#e0e0e0', borderRadius: '8px' }}>
            Clear
          </Button>
        )}
        <Button
          size="small" onClick={handleExport}
          disabled={exporting || filteredLoans.length === 0}
          startIcon={<FileDownloadIcon fontSize="small" />} variant="outlined"
          sx={{ textTransform: 'none', fontSize: '12px', borderRadius: '8px', borderColor: '#344054', color: '#344054', '&:hover': { backgroundColor: '#f5f5f5' } }}
        >
          Export CSV
        </Button>
      </Box>

      {/* ── Loans Table ── */}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              {[
                'Loan ID', 'Borrower', 'Phone', 'Bucket', 'DPD',
                'Total Overdue ₹', 'Paid ₹', 'Last Paid', 'Balance', <PhoneCallbackIcon key="phone-hdr" sx={{ fontSize: 16, color: '#667085' }} />,
                ...(isManagerPeek ? ['', ''] : ['', '', '', '']),
                'Profile',
              ].map((col, i) => (
                <TableCell key={i} sx={{ fontWeight: 600, color: '#667085', fontSize: '12px', whiteSpace: 'nowrap', py: '10px' }}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredLoans.length > 0 ? filteredLoans.map((loan, i) => {
              const bc = BUCKET_COLOR[loan.bucket] || { bg: '#f0f0f0', text: '#344054', border: '#e0e0e0' };
              const overdue = Number(loan.totalOverdue) || 0;
              const collected = Number(loan.totalCollected) || 0;
              const pos = Number(loan.pos) || 0;
              const isExpanded = expandedLoanId === loan.loanId;
              const isSendingNotify = sendingNotifyId === loan.loanId;

              return (
                <React.Fragment key={loan.loanId || i}>

                  {/* ── Main loan row ── */}
                  <TableRow sx={{ '&:hover': { backgroundColor: '#fafafa' }, backgroundColor: isExpanded ? '#fafbff' : 'transparent' }}>

                    {/* Loan ID — click to toggle handoff history */}
                    <TableCell sx={{ fontWeight: 700, color: '#344054', whiteSpace: 'nowrap' }}>
                      <Tooltip title={isExpanded ? 'Hide handoff history' : 'Show handoff history'} placement="top">
                        <Box
                          onClick={() => handleLoanIdClick(loan)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            cursor: 'pointer', userSelect: 'none',
                            color: isExpanded ? '#D22129' : '#344054',
                          }}
                        >
                          {loan.legacyLoanId || loan.loanId || '—'}
                          {isExpanded
                            ? <ExpandLessIcon style={{ fontSize: '14px', color: '#D22129' }} />
                            : <ExpandMoreIcon style={{ fontSize: '14px', color: '#9e9e9e' }} />
                          }
                        </Box>
                      </Tooltip>
                    </TableCell>

                    <TableCell sx={{ color: '#344054', whiteSpace: 'nowrap' }}>
                      {loan.borrowerName?.trim() || '—'}
                      {loan.instituteName && (
                        <Typography sx={{ fontSize: '10px', color: '#9e9e9e', mt: '1px' }}>{loan.instituteName}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: '#667085', fontSize: '12px', whiteSpace: 'nowrap' }}>
                      {loan.phone || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip label={loan.bucket || '—'} size="small"
                        sx={{ backgroundColor: bc.bg, color: bc.text, fontWeight: 700, fontSize: '11px', border: `1px solid ${bc.border}` }} />
                    </TableCell>
                    <TableCell>
                      {loan.dpd != null
                        ? <Chip label={`${loan.dpd}d`} size="small" sx={{ backgroundColor: bc.bg, color: bc.text, fontWeight: 700, fontSize: '11px' }} />
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {overdue + collected > 0 ? (
                        <span style={{ color: overdue > 0 ? '#d32028' : '#9e9e9e', fontWeight: overdue > 0 ? 700 : 400 }}>
                          {fmt(overdue + collected)}
                        </span>
                      ) : '—'}
                    </TableCell>
                    <TableCell sx={{ color: collected > 0 ? '#2e7d32' : '#bdbdbd', fontWeight: collected > 0 ? 700 : 400, whiteSpace: 'nowrap' }}>
                      {collected > 0 ? fmt(collected) : '—'}
                    </TableCell>
                    <TableCell sx={{ color: '#667085', fontSize: '12px', whiteSpace: 'nowrap' }}>
                      {fmtDate(loan.lastPaidDate)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {overdue > 0 ? (
                        <Box>
                          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#f57f17' }}>{fmt(overdue)}</Typography>
                          <LinearProgress
                            variant="determinate"
                            value={(overdue + collected) > 0 ? Math.min((collected / (overdue + collected)) * 100, 100) : 0}
                            sx={{
                              height: 4, borderRadius: 2, mt: '2px', width: 60,
                              backgroundColor: '#f0f0f0',
                              '& .MuiLinearProgress-bar': { backgroundColor: '#2e7d32', borderRadius: 2 },
                            }}
                          />
                        </Box>
                      ) : pos === 0 ? (
                        <span style={{ color: '#2e7d32', fontWeight: 700 }}>Cleared</span>
                      ) : collected > 0 ? (
                        <span style={{ color: '#2e7d32', fontWeight: 700, fontSize: '12px' }}>✓ Paid</span>
                      ) : (
                        <span style={{ color: '#9e9e9e' }}>—</span>
                      )}
                    </TableCell>

                    {/* ── Last Disposition badge ── */}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {(() => {
                        const disp = localDispositions[loan.loanId] || loan.lastDisposition || loan.callStatus;
                        const dc = DISP_BADGE[disp];
                        return (
                          <Box
                            onClick={() => !isManagerPeek && handleOpenCallModal(loan)}
                            sx={{ cursor: isManagerPeek ? 'default' : 'pointer', display: 'inline-block' }}
                          >
                            {dc ? (
                              <Box sx={{
                                display: 'inline-block', px: '8px', py: '2px', borderRadius: '4px',
                                fontSize: '11px', fontWeight: 700,
                                backgroundColor: dc.bg, color: dc.text,
                                '&:hover': !isManagerPeek ? { opacity: 0.75 } : {},
                              }}>
                                {disp}
                              </Box>
                            ) : (
                              <PhoneCallbackIcon sx={{ fontSize: 16, color: '#bdbdbd' }} />
                            )}
                          </Box>
                        );
                      })()}
                    </TableCell>

                    {/* ── Notification Bell ── */}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Notify borrower" placement="top">
                        <Box
                          component="span"
                          aria-label="Send notification"
                          onClick={(e) => {
                            if (isSendingNotify) return;
                            setNotifyMenu({ anchor: e.currentTarget, loan });
                          }}
                          sx={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 28, height: 28, borderRadius: '8px',
                            cursor: isSendingNotify ? 'default' : 'pointer',
                            backgroundColor: notifyMenu.loan?.loanId === loan.loanId ? '#fff0f0' : 'transparent',
                            border: notifyMenu.loan?.loanId === loan.loanId ? '1px solid #f48fb1' : '1px solid transparent',
                            transition: 'all 0.15s',
                            '&:hover': {
                              backgroundColor: '#fff0f0',
                              border: '1px solid #f48fb1',
                            },
                          }}
                        >
                          {isSendingNotify ? (
                            <CircularProgress size={14} sx={{ color: '#D22129' }} />
                          ) : (
                            <Badge
                              badgeContent=" "
                              color="error"
                              variant="dot"
                              sx={{
                                '& .MuiBadge-dot': { width: 7, height: 7, minWidth: 7, border: '1.5px solid white' },
                              }}
                            >
                              <NotificationsNoneIcon sx={{
                                fontSize: 17,
                                color: notifyMenu.loan?.loanId === loan.loanId ? '#D22129' : '#667085',
                              }} />
                            </Badge>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>

                    {/* ── Disposition actions — agent view only ── */}
                    {!isManagerPeek && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Promise to Pay">
                          <Box component="span" aria-label="Mark Promise to Pay" onClick={() => handlePtp(loan)} sx={{
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            px: 1, py: '4px', borderRadius: '6px', cursor: 'pointer',
                            fontSize: '11px', fontWeight: 700,
                            backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7',
                            '&:hover': { backgroundColor: '#c8e6c9' }, mr: '4px',
                          }}>
                            <HandshakeIcon sx={{ fontSize: 13 }} /> PTP
                          </Box>
                        </Tooltip>
                        <Tooltip title="Right Party No Response">
                          <Box component="span" aria-label="Mark Ring No Response" onClick={() => handleRnr(loan)} sx={{
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            px: 1, py: '4px', borderRadius: '6px', cursor: 'pointer',
                            fontSize: '11px', fontWeight: 700,
                            backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80',
                            '&:hover': { backgroundColor: '#ffe0b2' }, mr: '4px',
                          }}>
                            <DoNotDisturbIcon sx={{ fontSize: 13 }} /> RNR
                          </Box>
                        </Tooltip>
                      </TableCell>
                    )}
                    {!isManagerPeek && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Call Back">
                          <Box component="span" aria-label="Mark Call Back" onClick={() => handleCb(loan)} sx={{
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            px: 1, py: '4px', borderRadius: '6px', cursor: 'pointer',
                            fontSize: '11px', fontWeight: 700,
                            backgroundColor: '#e8eaf6', color: '#283593', border: '1px solid #9fa8da',
                            '&:hover': { backgroundColor: '#c5cae9' },
                          }}>
                            <PhoneCallbackIcon sx={{ fontSize: 13 }} /> CB
                          </Box>
                        </Tooltip>
                      </TableCell>
                    )}

                    {/* ── Payment Link ── */}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Generate Payment Link">
                        <Box component="span" aria-label="Generate payment link" onClick={() => setPaymentLinkModal({ open: true, loan })}
                          sx={{ display: 'inline-flex', cursor: 'pointer', color: '#555', '&:hover': { color: '#D22129' } }}>
                          <AddCircleOutlineIcon sx={{ fontSize: 20 }} />
                        </Box>
                      </Tooltip>
                    </TableCell>

                    {/* ── View Profile ── */}
                    <TableCell>
                      <Button
                        size="small" variant="contained"
                        onClick={() => window.open(`/Loanprofile?loanId=${loan.loanId}&legacyLoanId=${loan.legacyLoanId || loan.loanId}`, '_blank')}
                        sx={{
                          backgroundColor: '#d32028', fontSize: '11px', textTransform: 'none',
                          borderRadius: '6px', px: 1.5, py: '4px', whiteSpace: 'nowrap',
                          '&:hover': { backgroundColor: '#b71c1c' },
                        }}
                      >
                        View Profile
                      </Button>
                    </TableCell>
                  </TableRow>

                  {/* ── Handoff History expandable row ── */}
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={colCount} style={{ padding: 0, borderBottom: '2px solid #e0e0e0', backgroundColor: '#fafbff' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box style={{ padding: '4px 32px 16px 48px', borderLeft: '3px solid #D22129' }}>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingTop: '12px', marginBottom: '2px' }}>
                              <Typography style={{ fontSize: '13px', fontWeight: 700, color: '#D22129' }}>
                                {loan.legacyLoanId || loan.loanId}
                              </Typography>
                              <Typography style={{ fontSize: '12px', color: '#344054' }}>
                                {loan.borrowerName?.trim()}
                              </Typography>
                              {loan.bucket && (
                                <Box style={{
                                  padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                                  backgroundColor: bc.bg, color: bc.text, border: `1px solid ${bc.border}`,
                                }}>
                                  {loan.bucket}
                                </Box>
                              )}
                            </Box>
                            <HandoffHistoryPanel history={historyMap[loan.loanId]} loading={historyLoading[loan.loanId]} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}

                </React.Fragment>
              );
            }) : (
              <TableRow>
                <TableCell colSpan={colCount} sx={{ textAlign: 'center', py: 4, color: '#9e9e9e' }}>
                  {search ? 'No loans match your search.' : 'No loans assigned.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Pagination ── */}
      {totalFetchRows > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontSize: '12px', color: '#667085' }}>
              Showing {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, totalFetchRows)} of {totalFetchRows.toLocaleString('en-IN')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '12px', color: '#667085' }}>Rows:</Typography>
              <select
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                style={{ padding: '3px 8px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '12px', outline: 'none', cursor: 'pointer' }}
              >
                {[15, 25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </Box>
          </Box>
          <PaginationTable
            startPage={String(currentPage)}
            rowsPerPage={rowsPerPage}
            totalRows={totalFetchRows}
            paginate={(p) => setCurrentPage(p)}
          />
        </Box>
      )}

      {/* ── Notification Bell Dropdown Menu ── */}
      <Menu
        anchorEl={notifyMenu.anchor}
        open={Boolean(notifyMenu.anchor)}
        onClose={() => setNotifyMenu({ anchor: null, loan: null })}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 4,
          sx: {
            borderRadius: '12px',
            minWidth: 260,
            mt: '6px',
            border: '1px solid #f0f0f0',
            overflow: 'hidden',
          },
        }}
      >
        {/* Menu header — borrower name */}
        <Box sx={{ px: 2, py: '10px', borderBottom: '1px solid #f5f5f5', backgroundColor: '#fafafa' }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.6px', mb: '2px' }}>
            Notify Borrower
          </Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#344054', lineHeight: 1.3 }}>
            {notifyMenu.loan?.borrowerName?.trim() || '—'}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#9e9e9e' }}>
            {notifyMenu.loan?.phone || '—'} · {notifyMenu.loan?.legacyLoanId || notifyMenu.loan?.loanId}
          </Typography>
        </Box>

        {/* Notification options */}
        {NOTIFY_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.type}
            onClick={() => handleSendNotification(opt.type)}
            sx={{
              px: 2, py: '10px', gap: '12px',
              '&:hover': { backgroundColor: opt.hoverBg },
              borderBottom: '1px solid #fafafa',
            }}
          >
            {/* Icon box */}
            <Box sx={{
              width: 32, height: 32, borderRadius: '8px',
              backgroundColor: opt.iconBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {opt.icon}
            </Box>

            {/* Label + description */}
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054', lineHeight: 1.3 }}>
                {opt.label}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: '#9e9e9e', lineHeight: 1.3 }}>
                {opt.desc}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* ── Success / Error Toast ── */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px', fontSize: '13px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* ── Payment Link Modal ── */}
      <PaymentLinkModal
        open={paymentLinkModal.open}
        loan={paymentLinkModal.loan}
        onClose={() => setPaymentLinkModal({ open: false, loan: null })}
      />

      {/* ── Add Call Modal ── */}
      <Dialog open={callModal.open} onClose={() => setCallModal({ open: false, loan: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '15px', pb: 1 }}>
          Add Call — {callModal.loan?.legacyLoanId || callModal.loan?.loanId}
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <TextField
              label="Select Date and Time"
              type="datetime-local"
              fullWidth size="small"
              InputLabelProps={{ shrink: true }}
              value={callDate}
              onChange={(e) => setCallDate(e.target.value)}
            />
            <TextField
              label="Contact Name"
              fullWidth size="small"
              value={callContact}
              onChange={(e) => setCallContact(e.target.value)}
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <TextField
              label="Contact Number"
              fullWidth size="small"
              value={callPhone}
              onChange={(e) => setCallPhone(e.target.value)}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={callStatus} onChange={(e) => setCallStatus(e.target.value)} label="Status">
                <MenuItem value="PTP">PTP – Promise to Pay</MenuItem>
                <MenuItem value="RNR">RNR – Ring No Response</MenuItem>
                <MenuItem value="CB">CB – Call Back</MenuItem>
                <MenuItem value="Not Contactable">Not Contactable</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {callStatus === 'PTP' && (
            <TextField
              label="Promise Date"
              type="date"
              fullWidth size="small"
              InputLabelProps={{ shrink: true }}
              value={callPtpDate}
              onChange={(e) => setCallPtpDate(e.target.value)}
              inputProps={{ min: new Date().toISOString().slice(0, 10) }}
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            label="Remarks"
            fullWidth size="small" multiline rows={2}
            value={callRemarks}
            onChange={(e) => setCallRemarks(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <FormControlLabel
            control={<Checkbox checked={callReminder} onChange={(e) => setCallReminder(e.target.checked)} size="small" />}
            label={<Typography sx={{ fontSize: '13px' }}>🔔 Set reminder for this date/time</Typography>}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCallModal({ open: false, loan: null })} size="small" sx={{ textTransform: 'none', color: '#667085' }}>
            Cancel
          </Button>
          <Button
            onClick={handleCallSubmit}
            disabled={callSubmitting || (callStatus === 'PTP' && !callPtpDate)}
            variant="contained" size="small"
            sx={{ textTransform: 'none', backgroundColor: '#D22129', '&:hover': { backgroundColor: '#b71c1c' } }}
          >
            {callSubmitting ? 'Saving…' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── PTP Dialog ── */}
      <Dialog open={ptpDialog.open} onClose={() => setPtpDialog({ open: false, loan: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Inter-Medium', fontSize: '16px', pb: 1 }}>
          Promise to Pay — {ptpDialog.loan?.legacyLoanId || ptpDialog.loan?.loanId}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13px', color: '#667085', mb: 2 }}>
            {ptpDialog.loan?.borrowerName?.trim()} has promised to pay by:
          </Typography>
          <TextField
            fullWidth type="date" size="small" label="Promise Date"
            InputLabelProps={{ shrink: true }} value={ptpDate}
            onChange={(e) => setPtpDate(e.target.value)}
            inputProps={{ min: new Date().toISOString().slice(0, 10) }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPtpDialog({ open: false, loan: null })} size="small" sx={{ textTransform: 'none', color: '#667085' }}>
            Cancel
          </Button>
          <Button
            onClick={handlePtpSubmit} disabled={!ptpDate || ptpLoading}
            variant="contained" size="small"
            sx={{ textTransform: 'none', backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' } }}
          >
            {ptpLoading ? 'Saving…' : 'Confirm PTP'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
